<script setup>
  import { computed } from 'vue';
  import PriceSuffix from '../../PriceSuffix.vue';
  import InformationModal from '../../InformationModal';
  import InformationIcon from '../../InformationIcon';
  import { useConfiguratorApi } from '../../../composables/configurator';

  const emit = defineEmits(['select']);

  const props = defineProps({
    option: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
    selectedPaymentType: {
      type: String,
      required: true,
    },
    showInformation: {
      type: Boolean,
      default: false,
    },
  });

  const { getPrice, totalFormattedPrice } = useConfiguratorApi();

  const price = computed(() => {
    return getPrice(props.option);
  });

  const modalId = computed(() => {
    return `modal-${props.option.id.replace(/\s/g, '').replace('+', '-').replaceAll('#', '_')}`;
  });

  const isMonthlyCost = computed(() => {
    if (props.option.priceType === 'monthlyCost') return true;
    return false;
  });

  const absPrice = computed(() => {
    return Math.abs(price.value);
  });

  const numberSign = computed(() => {
    if (price.value < 0) return '-';
    return '+';
  });
</script>

<template>
  <div
    class="configurator-selection__color-option"
    :class="{ selected }"
  >
    <div class="configurator-selection__color-option-title">
      <h5 @click="emit('select', option)">
        {{ $i18n(option.value) }}
      </h5>
      <div
        v-if="showInformation && (option.information || option.informationImage)"
        :id="modalId"
        :data-analytics="`{'eventComponent':'configuration','eventType':'modal','eventLabel':'More information - ${$i18n(option.value, [], 'en-GB')} - ${option.id}'}`"
        :data-dd-action-name="`More information - ${$i18n(option.value, [], 'en-GB')} - ${option.id}`"
        class="sdds-u-inline-block"
      >
        <InformationIcon data-dd-action-name="more information" />
      </div>
    </div>
    <div
      class="configurator-selection__color-option--circle"
      :style="`background-color: ${option.description}`"
      @click="emit('select', option)"
    />
    <div
      class="configurator-selection__color-option-cost"
      @click="emit('select', option)"
    >
      <span class="configurator-selection__color-option-price">{{ numberSign }} {{ absPrice.toLocaleString('en').replace(/,/g,' ') }}</span>&nbsp;<PriceSuffix :price-suffix="props.selectedPaymentType === 'cash' && !isMonthlyCost ? totalFormattedPrice.priceSuffixOneTime : totalFormattedPrice.priceSuffixMonthly" />
    </div>
  </div>
  <InformationModal
    v-if="showInformation && (option.information || option.informationImage)"
    :id="modalId"
    :title="$i18n(option.value)"
    :information-text="$i18n(option.information)"
    :information-image="option.informationImage"
  />
</template>

<style scoped>
  h5 {
    margin: 0;
    text-align: center;
  }
  .configurator-selection__color-option {
    display: flex;
    flex-direction: column;
    place-items: center;
    gap: var(--sdds-spacing-element-8);
  }
  .configurator-selection__color-option:hover {
    cursor: pointer;
  }
  .configurator-selection__color-option.selected > .configurator-selection__color-option--circle {
    border-color: var(--sdds-blue);
  }
  .configurator-selection__color-option-title {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .configurator-selection__color-option--circle {
    width: 4em;
    height: 4em;
    border-radius: 50%;
    border: 3px solid transparent;
    transition: border-color 0.2s ease-out, outline 0.1s ease-out;
  }
  .configurator-selection__color-option:hover:not(.selected) > .configurator-selection__color-option--circle {
    border-color: var(--sdds-grey-300);
  }

  .configurator-selection__color-option-cost {
    font-size: 1.6em;
    vertical-align: top;
    font-family: "Scania Sans Regular", Arial, Helvetica, sans-serif;
    color: var(--sdds-grey-600);
    text-align: center;
  }
  .configurator-selection__color-option-price {
    color: var(--sdds-grey-500);
  }
  .priceSuffix__container {
    font-size: 0.4em;
    line-height: 1em;
          text-align: left;
  }
  .configurator-selection__color-option-price--included {
    font-size: 0.7em;
    color: var(--sdds-grey-500);
  }
</style>
